import { ExpandableSearch, type ExpandableSearchHandle } from "@salestrak/ui";
import React from "react";

import { HotkeyTooltipLabel } from "#app/components/keyboard-shortcuts/hotkey-tooltip-label";
import {
  useSlashSearchTarget,
  VIEW_SEARCH_SHORTCUT,
} from "#app/lib/hooks/use-slash-search";

type ViewSearchProps = Omit<
  React.ComponentProps<typeof ExpandableSearch>,
  "ref" | "tooltip"
> & {
  /** The collapsed trigger's tooltip name; the `/` keycap joins it while
   *  this search is the one the hotkey focuses. */
  tooltip?: string;
};

/**
 * THE search for a view's toolbar: the library's collapsed `ExpandableSearch`
 * registered as a `/` target, so the bare key opens and focuses it from
 * anywhere on the screen. One per view — the hotkey arbitrates between a
 * view's search, the settings sidebar's and an open dialog's by document
 * order (see `useSlashSearchTarget`), and a screen that shows several
 * searches at once (widgets) renders the bare library component instead.
 */
export function ViewSearch({
  tooltip = "Search in your view",
  ...props
}: ViewSearchProps): React.ReactElement {
  const ref = React.useRef<ExpandableSearchHandle>(null);
  const isTarget = useSlashSearchTarget(ref);
  return (
    <ExpandableSearch
      ref={ref}
      tooltip={
        <HotkeyTooltipLabel
          keys={isTarget ? VIEW_SEARCH_SHORTCUT : undefined}
          label={tooltip}
        />
      }
      {...props}
    />
  );
}
